import {
  DbExplorerStateAndActions,
  useDbExplorerStore,
} from "../../stores/dbExplorerStore";
import { useEffect, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import { Database, Filter, Loader2, X } from "lucide-react";
import React from "react";
import { ConnectionStringForm } from "./ConnectionStringForm";
import { DatabaseHeader } from "./DatabaseHeader";
import { DataTable } from "./DataTable";
import { FilterControls } from "./FilterControls";
import { Button } from "../ui/button";

export default function Datascope() {
  const {
    initializeConnectionString,
    isConnectionStringSet,
    selectedTable,
    error,
    tablesLoading,
    schemasLoading,
    integrationsLoading,
    isFiltersPanelOpen,
    setIsFiltersPanelOpen,
    appliedFilters,
  } = useDbExplorerStore(
    useShallow((state: DbExplorerStateAndActions) => ({
      initializeConnectionString: state.initializeConnectionString,
      isConnectionStringSet: state.isConnectionStringSet,
      selectedTable: state.selectedTable,
      error: state.error,
      tablesLoading: state.tablesLoading,
      schemasLoading: state.schemasLoading,
      integrationsLoading: state.integrationsLoading,
      isFiltersPanelOpen: state.isFiltersPanelOpen,
      setIsFiltersPanelOpen: state.setIsFiltersPanelOpen,
      appliedFilters: state.appliedFilters,
    })),
  );

  useEffect(() => {
    void initializeConnectionString();
  }, [initializeConnectionString]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.search) count++;
    const multiCount = appliedFilters.filters?.length || 0;
    if (multiCount > 0) {
      count += multiCount;
    } else if (
      appliedFilters.filterColumn &&
      (appliedFilters.filterValue || ["is_null", "not_null"].includes(appliedFilters.filterOperator))
    ) {
      count++;
    }
    if (appliedFilters.sortColumn) count++;
    return count;
  }, [appliedFilters]);

  if (integrationsLoading && !isConnectionStringSet) {
    return (
      <div className="container mx-auto p-6 flex flex-col justify-center items-center min-h-[calc(100vh-100px)] bg-gradient-to-br from-slate-50/30 via-white to-blue-50/20">
        <div className="p-8 rounded-md bg-white/80 border border-slate-200/60 shadow-sm text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
          <div className="space-y-2">
            <p className="text-slate-700 font-semibold text-lg">Loading database integrations</p>
            <p className="text-slate-500 text-sm">Checking connected databases for this workspace...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isConnectionStringSet) {
    return (
      <div className="container mx-auto p-6 flex justify-center items-start min-h-screen bg-gradient-to-br from-slate-50/30 via-white to-blue-50/20">
        <ConnectionStringForm />
      </div>
    );
  }

  if ((schemasLoading || tablesLoading) && !selectedTable) {
    return (
      <div className="container mx-auto p-6 flex flex-col justify-center items-center min-h-[calc(100vh-100px)] bg-gradient-to-br from-slate-50/30 via-white to-blue-50/20">
        <div className="p-8 rounded-md bg-white/80 border border-slate-200/60 shadow-sm text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
          <div className="space-y-2">
            <p className="text-slate-700 font-semibold text-lg">Connecting to Database</p>
            <p className="text-slate-500 text-sm">Loading database information...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="shrink-0 border-b bg-white px-4 md:px-8 py-2 flex items-center justify-between gap-4 max-w-full min-w-0">
        <div className="flex items-center gap-4 min-w-0">
          <h1 className="text-xl text-slate-800">Explore</h1>
          {selectedTable && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFiltersPanelOpen(!isFiltersPanelOpen)}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 h-8 text-xs"
            >
              {isFiltersPanelOpen ? (
                <>
                  <X className="h-4 w-4" />
                  <span className="hidden sm:inline">Close Filters</span>
                </>
              ) : (
                <>
                  <Filter className="h-4 w-4" />
                  <span className="hidden sm:inline">Filters</span>
                  {activeFiltersCount > 0 && (
                    <span className="ml-1 px-2 py-0.5 rounded-full bg-indigo-600 text-white text-xs">
                      {activeFiltersCount}
                    </span>
                  )}
                </>
              )}
            </Button>
          )}
        </div>

        <DatabaseHeader />
      </div>

      <div
        className={`flex-1 min-h-0 grid overflow-hidden w-full ${
          isFiltersPanelOpen
            ? "grid-cols-[320px_minmax(0,1fr)]"
            : "grid-cols-[0px_minmax(0,1fr)]"
        }`}
      >
        <div className="overflow-hidden border-r transition-all duration-300 ease-in-out">
          <div className="w-80 h-full overflow-y-auto">
            <FilterControls />
          </div>
        </div>

        <div className="min-h-0 min-w-0 flex flex-col overflow-hidden bg-slate-50">
          {selectedTable ? (
            <div className="h-full">
              <DataTable />
            </div>
          ) : (
            !tablesLoading && (
              <div className="flex-1 flex items-center justify-center h-full">
                <div className="text-center space-y-3 p-8 rounded-md bg-white/60 border border-slate-200/60 shadow-sm">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                    <Database className="h-8 w-8 text-slate-400" />
                  </div>
                  <p className="text-slate-600 font-medium text-lg">
                    {error ? "Could not load tables" : "Ready to explore"}
                  </p>
                  <p className="text-slate-500 text-sm max-w-md">
                    {error
                      ? "Please check your connection or error message above."
                      : "Select a table using the controls above to view its data and start exploring your database."}
                  </p>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
